import { HttpParams } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Hydra } from 'src/app/interfaces/hydra';
import { Participation } from '../../interfaces/participation';
import { ParticipationService } from '../../services/participation.service';
import { CardType } from 'src/app/enums/card-type';
import { Project } from 'src/app/interfaces/project';
import { ProjectService } from 'src/app/services/project.service';
import { SurveyService } from 'src/app/services/survey.service';
import { RatingService } from 'src/app/services/rating.service';
import { ApiInit } from 'src/app/utils/api-init';
import { ParticipationCounts } from '../../interfaces/participation-counts';
import { ParticipationItemType } from '../../enums/participation-item-type';
import { PollService } from 'src/app/services/poll.service';
import { Update } from 'src/app/interfaces/update';
import { DeviceService } from '../../services/device.service';
import { LoaderComponent } from '../../components/loader/loader.component';
import { CardComponent } from '../../components/cards/card/card.component';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { NgIf, NgFor } from '@angular/common';
import {
  IonContent,
  IonRefresher,
  IonRefresherContent,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonHeader,
  IonToolbar,
  IonTitle,
} from '@ionic/angular/standalone';
import { DivClickDirective } from 'src/app/directives/div-click.directive';
import { StorageService } from 'src/app/services/storage.service';

@Component({
  selector: 'app-participation',
  templateUrl: './participation.page.html',
  standalone: true,
  imports: [
    NgIf,
    NgxSkeletonLoaderModule,
    InlineSVGModule,
    NgFor,
    CardComponent,
    LoaderComponent,
    TranslateModule,
    IonContent,
    IonRefresher,
    IonRefresherContent,
    IonInfiniteScroll,
    IonInfiniteScrollContent,
    DivClickDirective,
    IonHeader,
    IonToolbar,
    IonTitle,
  ],
})
export class ParticipationPage implements OnInit {
  CardType = CardType;
  participationItems: Participation[] = [];
  loading: boolean = true;
  projects: Project[];
  page: number = 1;
  perPage: number = 7;
  totalItems: number;
  openCounts: ParticipationCounts;
  filter: ParticipationItemType = null;
  showConfetti: boolean = false;

  ParticipationItemType = ParticipationItemType;

  constructor(
    private participationService: ParticipationService,
    private surveyService: SurveyService,
    private ratingService: RatingService,
    private projectService: ProjectService,
    private deviceService: DeviceService,
    private pollService: PollService,
    private apiInit: ApiInit,
    private storageService: StorageService
  ) {}

  async ngOnInit() {
    this.projectService.starChanged$.subscribe(() => {
      setTimeout(() => {
        this.page = 1;
        this.loadParticipation();
      }, 500);
    });

    this.surveyService.surveySubmitted.subscribe((surveyId) => {
      this.participationItems = this.participationItems.filter(
        (item) => item.survey?.id != surveyId
      );
      this.openCounts.surveyCount--;
      this.openCounts.totalCount--;
      this.totalItems--;
    });

    this.ratingService.ratingSubmitted.subscribe((projectId) => {
      if (projectId === null) {
        return;
      }

      this.participationItems = this.participationItems.filter(
        (item) => !(item.project?.id == projectId && item.type === 'rating')
      );
      this.openCounts.ratingCount--;
      this.openCounts.totalCount--;
      this.totalItems--;
    });

    this.pollService.pollVoted.subscribe(() => {
      this.openCounts.pollCount--;
      this.openCounts.totalCount--;
    });

    this.apiInit.watch(async () => {
      await this.loadParticipation();
    });
  }

  async loadParticipation(event = null) {
    this.storageService
      .get('hasSeenParticipationCount')
      .then((value) => (this.showConfetti = value));

    this.loadOpenCounts();
    await this.loadOpenItems();
    this.getDeviceData();

    if (event) {
      event.target.complete();
    }
  }

  async getDeviceData() {
    let updates: Update[] = [];
    this.participationItems.forEach((item) => {
      if (item.type === ParticipationItemType.POLL) {
        updates.push(item.pollUpdate);
      }
    });

    try {
      const response = await this.deviceService.getData(
        new HttpParams().set(
          'update_ids',
          updates.map(({ id }) => id).join(',')
        )
      );
      let updateData = response.updates;
      if (updateData) {
        Object.keys(updateData).forEach((updateId) => {
          let _update = updates.find((x) => x.id == parseInt(updateId));
          _update.liked = updateData[updateId].liked;
          _update.likedType = updateData[updateId].likedType;
        });
      }
    } catch (error) {}
  }

  async loadOpenItems(event = null) {
    let params = new HttpParams()
      .set('page', this.page.toString())
      .set('perPage', this.perPage.toString());

    if (this.filter) {
      params = params.set('type', this.filter);
    }

    try {
      const response: Hydra<Participation> =
        await this.participationService.getOpenItems(params, true);
      if (!this.participationItems?.length || this.page == 1) {
        this.participationItems = response.member;
      } else {
        this.participationItems = this.participationItems.concat(
          response.member
        );
      }
      this.totalItems = response.totalItems;
      this.page++;
    } catch (error) {
    } finally {
      this.loading = false;
      if (event) event.target.complete();
    }
  }

  async loadOpenCounts() {
    try {
      this.openCounts = await this.participationService.getOpenCounts();
      if (this.openCounts.totalCount > 0) {
        this.storageService.set('hasSeenParticipationCount', true);
      }
    } catch (error) {
      // ignore
    }
  }

  handleRefresh(event) {
    this.page = 1;
    this.loadParticipation(event);
  }

  toggleFilter(filter: ParticipationItemType) {
    this.filter = this.filter === filter ? null : filter;
    this.participationItems = [];
    this.loading = true;
    this.page = 1;
    this.loadOpenItems();
  }
}
